/**
 * P0.8 - NO RAW RESPONSE ENFORCEMENT
 * 
 * This is the ONLY function that reads response body.
 * No clone() - direct read since apiFetch already verified bodyUsed=false.
 * 
 * Contract:
 * - Called ONLY from apiFetch (which already checks bodyUsed)
 * - Reads body directly (no clone needed)
 * - Returns parsed result, NEVER a Response
 */

/**
 * Safely read a fetch Response.
 * 
 * PRECONDITION: Caller (apiFetch) has already verified res.bodyUsed === false
 * Therefore we can read directly without clone.
 * 
 * @param {Response} res - The fetch Response object (bodyUsed MUST be false)
 * @returns {Promise<{ok, status, traceId, json, rawText, error}>}
 */
export async function safeReadResponse(res) {
  // Get trace_id from headers (safe, doesn't consume body)
  const traceId = res?.headers?.get('x-trace-id') || null;
  
  if (!res) {
    return {
      ok: false,
      traceId: null,
      status: 0,
      json: null,
      rawText: null,
      error: { code: 'NULL_RESPONSE', message: 'Response object is null' }
    };
  }
  
  // P0.8: Read body DIRECTLY - no clone needed
  // apiFetch already verified bodyUsed=false before calling us
  let text = null;
  try {
    text = await res.text();
  } catch (readErr) {
    console.error('[safeReadResponse] P0.8: text() failed:', readErr.message);
    return {
      ok: false,
      traceId,
      status: res.status,
      json: null,
      rawText: null,
      error: {
        code: 'READ_FAILED',
        message: `Failed to read response: ${readErr.message}`
      }
    };
  }
  
  // Parse JSON if possible
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    // Not valid JSON - that's okay
  }
  
  // Prefer trace_id from JSON body if present
  const finalTraceId = (json && typeof json === 'object' && json.trace_id) || traceId;
  
  return {
    ok: res.ok,
    traceId: finalTraceId,
    status: res.status,
    json,
    rawText: text,
    error: null
  };
}


/**
 * Extract a user-friendly error message from a parsed response.
 */
export function extractErrorMessage(parsed) {
  const { status, json, rawText, error } = parsed;
  
  if (error) {
    return error.message || `Request failed with status ${status}`;
  }
  
  if (json && typeof json === 'object') {
    const message = 
      json.message ||
      json.detail?.message ||
      json.error?.message ||
      (typeof json.detail === 'string' ? json.detail : null) ||
      (typeof json.error === 'string' ? json.error : null) ||
      json.reason ||
      json.detail?.reason;
    
    if (message) return message;
    
    if (json.error && typeof json.error === 'object') {
      return json.error.message || JSON.stringify(json.error);
    }
    if (json.detail && typeof json.detail === 'object') {
      return json.detail.message || JSON.stringify(json.detail);
    }
  }
  
  if (typeof rawText === 'string' && rawText.length > 0) {
    return rawText.length > 200 ? rawText.substring(0, 200) + '...' : rawText;
  }
  
  return `Request failed with status ${status}`;
}


/**
 * Extract structured error info for logging.
 */
export function extractErrorInfo(parsed) {
  const { status, json, rawText, traceId, error } = parsed;
  
  if (error) {
    return {
      status,
      traceId,
      error: error.code || 'PARSE_ERROR',
      message: error.message,
      isParseError: true
    };
  }
  
  if (json && typeof json === 'object') {
    return {
      status,
      traceId: traceId || json.trace_id || null,
      error: json.error || json.detail?.error || 'UNKNOWN',
      message: json.message || json.detail?.message || json.detail || null,
      reason: json.reason || json.detail?.reason || null,
      isParseError: false
    };
  }
  
  return {
    status,
    traceId,
    error: 'NON_JSON_RESPONSE',
    message: typeof rawText === 'string' ? rawText.substring(0, 500) : null,
    isParseError: false
  };
}


/**
 * Fetch custody chain for a failed TTS operation.
 * P0.9: Uses xhrFetchAuth to bypass fetch interceptor.
 */
export async function fetchCustodyChain(titleId, traceId, backendUrl) {
  if (!traceId || !titleId) {
    return { ok: false, events: [], error: 'Missing titleId or traceId' };
  }
  
  try {
    // P0.9: Use XMLHttpRequest via xhrFetchAuth to bypass fetch interceptor
    const { xhrFetchAuth } = await import('./xhrFetch');
    
    const r = await xhrFetchAuth(
      `${backendUrl}/api/publisher/titles/${titleId}/tts/custody?trace_id=${encodeURIComponent(traceId)}`
    );
    
    if (!r.ok || r.error) {
      return { 
        ok: false, 
        events: [], 
        error: extractErrorMessage(r)
      };
    }
    
    return {
      ok: true,
      events: r.json?.events || [],
      error: null
    };
  } catch (e) {
    console.error('[fetchCustodyChain] Failed:', e);
    return { ok: false, events: [], error: e.message };
  }
}
